import { createFileRoute, Link } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPolicy,
});

function PrivacyPolicy() {
  return (
    <div className="bg-topography min-h-svh">
      <div className="container mx-auto max-w-4xl px-6 py-12 md:py-16">
        <div className="mb-6">
          <Link
            to="/"
            className="text-muted-foreground hover:text-primary text-sm underline underline-offset-4"
          >
            ← Back to Home
          </Link>
        </div>

        <Card>
          <CardContent className="p-8 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <img src="/cte-logo.png" alt="CTE Logo" className="h-10 w-auto" />
              <h1 className="text-3xl font-bold">Privacy Policy</h1>
            </div>

            <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6">
              <p className="text-muted-foreground">
                Last updated: December 2, 2025
              </p>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold">1. Introduction</h2>
                <p>
                  CTE ("Company", "we", "us", or "our") is committed to
                  protecting your privacy. This Privacy Policy explains how we
                  collect, use, disclose, and safeguard your information when
                  you use our iPlant platform and related services.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold">
                  2. Information We Collect
                </h2>
                <p>We may collect the following types of information:</p>
                <h3 className="text-lg font-medium mt-4">
                  Personal Information
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Name and contact information (email address, phone number)
                  </li>
                  <li>Account credentials</li>
                  <li>Professional information (job title, organization)</li>
                  <li>Communication preferences</li>
                </ul>
                <h3 className="text-lg font-medium mt-4">Usage Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Device and browser information</li>
                  <li>IP address and location data</li>
                  <li>Usage patterns and preferences</li>
                  <li>Log data and analytics</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold">
                  3. How We Use Your Information
                </h2>
                <p>We use the information we collect to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide and maintain our services</li>
                  <li>Authenticate users and manage accounts</li>
                  <li>Improve and personalize user experience</li>
                  <li>Communicate with you about updates and support</li>
                  <li>Ensure security and prevent fraud</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold">
                  4. Information Sharing
                </h2>
                <p>
                  We do not sell your personal information. We may share your
                  information in the following circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    With your organization's administrators (for enterprise
                    accounts)
                  </li>
                  <li>With service providers who assist in our operations</li>
                  <li>When required by law or to protect our rights</li>
                  <li>In connection with a business transfer or merger</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold">5. Data Security</h2>
                <p>
                  We implement appropriate technical and organizational measures
                  to protect your personal information against unauthorized
                  access, alteration, disclosure, or destruction. These measures
                  include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments and audits</li>
                  <li>Access controls and authentication requirements</li>
                  <li>Employee training on data protection</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold">6. Data Retention</h2>
                <p>
                  We retain your personal information for as long as necessary
                  to fulfill the purposes outlined in this Privacy Policy,
                  unless a longer retention period is required or permitted by
                  law.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold">7. Your Rights</h2>
                <p>Depending on your location, you may have the right to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to or restrict processing</li>
                  <li>Data portability</li>
                  <li>Withdraw consent</li>
                </ul>
                <p>
                  To exercise these rights, please contact us using the
                  information provided below.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold">
                  8. Cookies and Tracking
                </h2>
                <p>
                  We use cookies and similar technologies to enhance your
                  experience, analyze usage, and assist in our marketing
                  efforts. You can manage your cookie preferences through your
                  browser settings.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold">
                  9. Changes to This Policy
                </h2>
                <p>
                  We may update this Privacy Policy from time to time. We will
                  notify you of any changes by posting the new Privacy Policy on
                  this page and updating the "Last updated" date.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold">10. Contact Us</h2>
                <p>
                  If you have any questions about this Privacy Policy or our
                  data practices, please contact us at{" "}
                  <a
                    href="mailto:privacy@cte.com"
                    className="text-primary underline underline-offset-4"
                  >
                    privacy@cte.com
                  </a>
                  .
                </p>
              </section>

              <div className="pt-8 border-t">
                <p className="text-sm text-muted-foreground">
                  See also our{" "}
                  <Link
                    to="/terms"
                    className="text-primary underline underline-offset-4"
                  >
                    Terms of Service
                  </Link>
                  .
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
